import { Dispatch, SetStateAction, useEffect } from 'react'
import { UseFormClearErrors, UseFormSetError, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { IBAN_VALIDATION_ERRORS } from 'pages/ManageBeneficiaries/constants'
import {
  BaseErrorResponse,
  BeneficiaryDetails,
  BeneficiaryDetailsCurrency,
  PaymentMethod,
  ValidateBicSwiftCodeResponse,
  ValidateIbanResponse,
  ValidateRoutingNumberResponse,
  ValidateScanResponse,
} from 'types/beneficiaries'
import { MappedErrorMessageState } from 'types/errors'
import { ContactFiatInstrumentFormData } from '../schemas/contactFiatInstrumentSchema'
import { AddressType, FieldsFetchedFromIbanValidation } from '../types/fiatInstruments'
import {
  getIsValidIban,
  resetBankAddress,
  setBankAccountFieldsFetchedFromIbanValidation,
} from '../utils/fiatInstruments'

type IbanValidationProps = {
  ibanBankDetails: ValidateIbanResponse
  isValidatingIbanError: boolean
  setIsValidIban: Dispatch<SetStateAction<boolean>>
  setIbanBankDetailsData: (details: FieldsFetchedFromIbanValidation) => void
}

// A single BIC validation config — one per field that needs BIC/SWIFT validation.
// addressSection controls which address fields get autofilled on success; null means no autofill.
export type BicValidationConfig = {
  bicValidationData?: ValidateBicSwiftCodeResponse
  isValidatingBicError: boolean
  bicValidationError?: BaseErrorResponse
  setIsValidBic: Dispatch<SetStateAction<boolean>>
  fieldName: keyof ContactFiatInstrumentFormData
  addressSection: Exclude<AddressType, 'beneficiary'> | null
}

type RoutingNumberValidationProps = {
  routingNumberBankDetails: ValidateRoutingNumberResponse
  isValidatingRoutingNumberError: boolean
  setIsRoutingNumberValid: Dispatch<SetStateAction<boolean>>
}

type ScanValidationProps = {
  scanBankDetails: ValidateScanResponse
  isValidatingScanError: boolean
  setIsValidScan: Dispatch<SetStateAction<boolean>>
}

type UseBankDetailValidationProps = {
  // IBAN
  ibanValidationProps?: IbanValidationProps
  // BIC — one entry per field to validate (e.g. bankCode, swiftCode, intermediaryBankSwiftCode)
  bicValidationConfigs?: BicValidationConfig[]
  // Routing number
  routingNumberValidationProps?: RoutingNumberValidationProps
  // Scan
  scanValidationProps?: ScanValidationProps
  // Form
  formValues: ContactFiatInstrumentFormData
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
  setError: UseFormSetError<ContactFiatInstrumentFormData>
  clearErrors: UseFormClearErrors<ContactFiatInstrumentFormData>
  setRequestError?: Dispatch<SetStateAction<MappedErrorMessageState>>
  // Beneficiary
  contactBeneficiary?: BeneficiaryDetails
  // IBAN state
  isIbanFormat?: boolean
  isValidIban?: boolean
}

export const useBankDetailValidation = ({
  ibanValidationProps,
  bicValidationConfigs,
  routingNumberValidationProps,
  scanValidationProps,
  formValues,
  setValue,
  setError,
  clearErrors,
  contactBeneficiary,
  isIbanFormat = false,
  isValidIban = false,
}: UseBankDetailValidationProps) => {
  const { t } = useTranslation()

  // IBAN validation
  useEffect(() => {
    if (!ibanValidationProps) return
    if (contactBeneficiary) return

    const { isValidatingIbanError, ibanBankDetails, setIsValidIban, setIbanBankDetailsData } =
      ibanValidationProps

    if (!ibanBankDetails) return

    clearErrors()

    const isValidIban = getIsValidIban({
      paymentMethods: formValues?.paymentMethods as PaymentMethod[],
      currency: formValues?.currency,
      ibanBankDetails,
    })

    if (!isValidIban && !isValidatingIbanError && formValues?.accountNumber) {
      const countryDoesNotSupportIban = ibanBankDetails?.validationFailed?.includes(
        IBAN_VALIDATION_ERRORS.COUNTRY_DOES_NOT_SUPPORT_IBAN
      )

      if (!countryDoesNotSupportIban) {
        setIsValidIban(false)
        setValue('bankAddressLine1', '')
        setValue('bankAddressLine2', '')
        setValue('bankCity', '')
        setValue('bankRegion', '')
        setValue('bankPostCode', '')
        setValue('bankCode', '')
        setValue('bankName', '')
        setValue('bankCountryCode', '')
      }
      return
    }

    if (isValidIban) {
      setIsValidIban(true)
    }

    const details = setBankAccountFieldsFetchedFromIbanValidation(formValues, ibanBankDetails)
    setIbanBankDetailsData(details)
  }, [
    ibanValidationProps?.ibanBankDetails,
    ibanValidationProps?.isValidatingIbanError,
    formValues?.accountNumber,
    formValues?.currency,
    clearErrors,
    contactBeneficiary,
  ])

  const populateBankDetails = (
    addressSection: Exclude<AddressType, 'beneficiary'>,
    bicValidationData: ValidateBicSwiftCodeResponse
  ) => {
    const { bankName, bankAddress } = bicValidationData?.bankDetails || {}
    const { addressLine1, addressLine2, city, region, postCode, countryCode } = bankAddress || {}

    if (addressSection === 'bank') {
      setValue(`bankName`, bankName)
      setValue(`bankAddressLine1`, addressLine1)
      setValue(`bankAddressLine2`, addressLine2)
      setValue(`bankCity`, city)
      setValue(`bankRegion`, region)
      setValue(`bankPostCode`, postCode)
      setValue(`bankCountryCode`, countryCode)
      return
    }

    if (addressSection === 'intermediaryBank') {
      setValue(`intermediaryBankName`, bankName)
      setValue(`intermediaryBankAddressLine1`, addressLine1)
      setValue(`intermediaryBankAddressLine2`, addressLine2)
      setValue(`intermediaryBankCity`, city)
      setValue(`intermediaryBankRegion`, region)
      setValue(`intermediaryBankPostCode`, postCode)
      setValue(`intermediaryBankCountryCode`, countryCode)
    }
  }

  const applyBicConfig = (config: BicValidationConfig) => {
    const {
      bicValidationData,
      isValidatingBicError,
      bicValidationError,
      setIsValidBic,
      fieldName,
      addressSection,
    } = config
    const fieldValue = formValues?.[fieldName]

    if (isValidatingBicError) {
      const errorMessage =
        bicValidationError?.data?.errorList?.[0]?.message ??
        bicValidationError?.errorList?.[0]?.message ??
        t('bicValidationError')
      setError(fieldName, { message: errorMessage })
      setIsValidBic(false)
      return
    }

    if (!bicValidationData || !fieldValue) {
      setIsValidBic(false)
      return
    }

    const isValidBic =
      !!bicValidationData?.bankDetails?.bankName &&
      !!bicValidationData?.bankDetails?.bankAddress &&
      !bicValidationData?.validationFailed?.length

    if (isValidBic) {
      clearErrors([fieldName])
      setIsValidBic(true)
      if (addressSection) populateBankDetails(addressSection, bicValidationData)
    } else {
      setError(fieldName, { message: t('invalidBicCodeErrorMessage') })

      if (addressSection) {
        resetBankAddress(setValue, addressSection)
      }

      setIsValidBic(false)
    }
  }

  const bicValidationDeps =
    bicValidationConfigs?.flatMap(config => [
      config.bicValidationData,
      config.isValidatingBicError,
      config.bicValidationError,
    ]) ?? []

  useEffect(() => {
    if (!bicValidationConfigs?.length) return
    if (contactBeneficiary) return

    bicValidationConfigs.forEach(applyBicConfig)
  }, [...bicValidationDeps, contactBeneficiary])

  // Routing number validation (USD bank code)
  useEffect(() => {
    if (!routingNumberValidationProps) return
    if (contactBeneficiary) return

    const { isValidatingRoutingNumberError, routingNumberBankDetails, setIsRoutingNumberValid } =
      routingNumberValidationProps

    if (formValues?.currency !== BeneficiaryDetailsCurrency.USD) return
    if (!routingNumberBankDetails) return

    const { bankDetails, validationFailed } = routingNumberBankDetails
    const { bankAddress, bankName } = bankDetails ?? {}

    if (isValidatingRoutingNumberError) {
      setError('bankCode', { message: t('routingNumberValidationError') })
      setIsRoutingNumberValid(false)
      return
    }

    const isValid = !!bankAddress && !validationFailed?.length

    setIsRoutingNumberValid(isValid)

    if (isValid) {
      clearErrors()
      if (!isIbanFormat || !isValidIban) {
        const { addressLine1, addressLine2, city, region, postCode } = bankAddress || {}
        setValue('bankName', bankName)
        setValue('bankAddressLine1', addressLine1)
        setValue('bankAddressLine2', addressLine2)
        setValue('bankCity', city)
        setValue('bankRegion', region)
        setValue('bankPostCode', postCode)
      }
    } else {
      formValues?.bankCode && setError('bankCode', { message: t('abaRoutingNumberError') })
      if (!isIbanFormat || !isValidIban) {
        resetBankAddress(setValue, 'bank')
        setValue('bankName', '')
      }
    }
  }, [
    routingNumberValidationProps?.routingNumberBankDetails,
    routingNumberValidationProps?.isValidatingRoutingNumberError,
    formValues?.currency,
    contactBeneficiary,
    isIbanFormat,
    isValidIban,
  ])

  // Scan validation (GBP)
  useEffect(() => {
    if (!scanValidationProps) return
    if (contactBeneficiary) return

    if (formValues?.currency !== BeneficiaryDetailsCurrency.GBP) return

    const { scanBankDetails, isValidatingScanError, setIsValidScan } = scanValidationProps
    const { supportedPaymentMethods } = scanBankDetails ?? {}

    if (!scanBankDetails) return

    const isFPSandChapsSupported =
      supportedPaymentMethods?.includes('CHAPS') ||
      supportedPaymentMethods?.includes('BACS') ||
      supportedPaymentMethods?.includes('FASTER_PAYMENT') ||
      supportedPaymentMethods?.includes('CCC_PAYMENTS')

    if (isValidatingScanError) {
      setError('accountNumber', { message: t('scanValidationError') })
      setError('bankCode', { message: t('scanValidationError') })
      setIsValidScan(false)
      return
    }

    if (scanBankDetails && !isFPSandChapsSupported) {
      setError('accountNumber', { message: t('reachabilityCheckErrorMessage') })
      setError('bankCode', { message: t('reachabilityCheckErrorMessage') })
      setIsValidScan(false)
      return
    }

    setIsValidScan(true)
  }, [
    scanValidationProps?.scanBankDetails,
    scanValidationProps?.isValidatingScanError,
    formValues?.currency,
    contactBeneficiary,
  ])
}
